/** @jsxImportSource @emotion/react */
import { css, keyframes } from '@emotion/react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import 'react-medium-image-zoom/dist/styles.css'
import { OrderItemRich } from '../OrderItemRich'
import { Divider, Icon } from 'semantic-ui-react'
import { isStatusAtLeast } from '../../../../../services/Order/order.helper'
import { Virtuoso } from 'react-virtuoso'
import { NAVBAR_HEIGHT_IN_PX } from '../../../../NavBar'


const isNotTreated = (item) =>
  item.quantity !== 0 &&
  item.fulfillmentIssue !== 'OUT_OF_ITEM' &&
  item.fulfillmentIssue !== 'PARTIAL_AVAILABILITY' &&
  !item.pickingMethod

const virtualItemPlaceholder = ({height}) => (
  <div style={{height, padding: '4px 0'}}>
      <div css={css`
          height: 100%;
          background: linear-gradient(90deg, #ececec 25%, #e0e0e0 50%, #ececec 75%);
          background-size: 200% 100%;
          border-radius: 4px;
          animation: ${keyframes`
              0% {
                  background-position: 200% 0;
              }
              100% {
                  background-position: -200% 0;
              }
          `} 1.4s ease-in-out infinite;
      `}/>
  </div>
)


const OrderItemsRich = ({
                            order,
                            hideable,
                            virtuosoRef: externalVirtuosoRef,
                        }) => {

    const [expandState, setExpandState] = useState(isStatusAtLeast(order?.status, 'READY') ? 'EXPAND' : 'COLLAPSE')

    const filteredItems = useMemo(
      () => order.orderItems.filter(orderItem => !orderItem.fulfillmentIssueParentId),
      [order.orderItems],
    )

    // Precompute replacements map once for big orders
    const relatedReplacementsMap = useMemo(() => {
        const map = new Map()
        order.orderItems.forEach(item => {
            if (item.fulfillmentIssueParentId) {
                const list = map.get(item.fulfillmentIssueParentId) ?? []
                list.push(item)
                map.set(item.fulfillmentIssueParentId, list)
            }
        })
        return map
    }, [order.orderItems])

    const internalVirtuosoRef = useRef(null)
    const virtuosoRef = externalVirtuosoRef ?? internalVirtuosoRef
    const filteredItemsRef = useRef(filteredItems)
    filteredItemsRef.current = filteredItems

    const initialFirstUncheckedIndex = useRef(filteredItems.findIndex(isNotTreated))

    const scrollToItemByIndex = useCallback((index) => {
        const item = filteredItemsRef.current[index]
        if (!item) return

        const element = document.getElementById(`orderItem-${item.id}`)
        if (element) {
            const y = element.getBoundingClientRect().top + window.scrollY - NAVBAR_HEIGHT_IN_PX
            window.scrollTo({top: y, behavior: 'smooth'})
        } else {
            virtuosoRef.current?.scrollToIndex({
                index,
                align: 'start',
                behavior: 'smooth',
                offset: -NAVBAR_HEIGHT_IN_PX,
            })
        }
    }, [])

    useEffect(() => {
        const idx = initialFirstUncheckedIndex.current
        if (idx <= 0) return

        const timer = setTimeout(() => scrollToItemByIndex(idx), 300)
        return () => clearTimeout(timer)
    }, [scrollToItemByIndex])

    const handleItemChecked = useCallback((orderItemId) => {
        const items = filteredItemsRef.current
        const currentIndex = items.findIndex(item => item.id === orderItemId)
        if (currentIndex === -1) return

        const nextIndex = items.findIndex((item, idx) => idx > currentIndex && isNotTreated(item))
        if (nextIndex === -1) return

        setTimeout(() => scrollToItemByIndex(currentIndex), 300)
    }, [scrollToItemByIndex])

    const renderItem = useCallback((index, orderItem) => (
      <>
          <OrderItemRich
            order={order}
            orderItem={orderItem}
            hideable={hideable}
            isChecked={orderItem.quantity === 0 ||
              orderItem.fulfillmentIssue === 'OUT_OF_ITEM' ||
              orderItem.fulfillmentIssue === 'PARTIAL_AVAILABILITY' ||
              !!orderItem.pickingMethod}
            expandState={expandState}
            relatedReplacements={relatedReplacementsMap.get(orderItem.id) ?? []}
            onItemChecked={handleItemChecked}
          />

          {index < filteredItems.length - 1 && (
            <div css={css`
                padding: 1rem 0;
            `}>
                <Divider css={css`
                    &.ui.divider {
                        margin: 0;
                    }
                `}/>
            </div>
          )}
      </>
    ), [order, hideable, expandState, relatedReplacementsMap, handleItemChecked, filteredItems.length])

    return (
      <>
          {order.orderItems.some(orderItem => !!orderItem.pickingMethod) &&
            <div style={{textAlign: 'right', marginRight: '.7rem', marginBottom: '10px'}}>
                {expandState === 'COLLAPSE' && <Icon
                  color="grey"
                  onClick={() => setExpandState('EXPAND')}
                  name="folder"
                />}

                {expandState === 'EXPAND' && <Icon
                  color="grey"
                  onClick={() => setExpandState('COLLAPSE')}
                  name="folder open"
                />}
            </div>
          }
          <Virtuoso
            ref={virtuosoRef}
            useWindowScroll
            data={filteredItems}
            itemContent={renderItem}
            overscan={800}
            initialTopMostItemIndex={initialFirstUncheckedIndex.current > 0 ? initialFirstUncheckedIndex.current : 0}
            scrollSeekConfiguration={{
                enter: velocity => Math.abs(velocity) > 600,
                exit: velocity => Math.abs(velocity) < 50,
            }}
            components={{ScrollSeekPlaceholder: virtualItemPlaceholder}}
          />
      </>
    )
}

OrderItemsRich.propTypes = {
    orderItemsWithFulfillmentIssue: PropTypes.array,
    order: PropTypes.object.isRequired,
}

export default OrderItemsRich
